import { TrendIconEnum } from '@/components/atoms/TrendIcon/TrendIconUtils';
import settings from '@/config/settings';
import type { SwimSpeedOverview, WelfareOverview } from '@/domain/cage-welfare-overview';
import { formatFractionAsPercentageByLocale, formatNumberByLocale } from '@/utils/localeFormatters';
import {
    calculateFishSpeedToolTip,
    getSpeedStatus,
} from '@/utils/SharedDataCalculation/swimSpeedCalculations';

const NO_VALUE = '-';

type Translate = (key: string) => string;

export type WelfareRowKey = 'wounds' | 'swimSpeed';

/** A key/value list, rendered through `TooltipRows`. */
export type WelfareTooltipTable = { key: string; value: string }[];

/** One welfare metric as the card draws it. Everything is already translated and formatted. */
export interface WelfareSummaryRow {
    key: WelfareRowKey;
    label: string;
    value: string;
    trend: TrendIconEnum;
    /** Absent when there is nothing to explain. */
    tooltip?: string | WelfareTooltipTable;
}

function isMissing(value: number | undefined): value is undefined {
    return value === undefined || Number.isNaN(value);
}

/** More wounds than last week is bad news, so a rising slope is drawn in the warning colour. */
function woundTrend(slope: number | undefined, limit: number): TrendIconEnum {
    if (slope === undefined) return TrendIconEnum.TrendStable;
    if (slope >= limit) return TrendIconEnum.TrendNegativeUp;
    if (slope <= -limit) return TrendIconEnum.TrendPositiveDown;
    return TrendIconEnum.TrendStable;
}

function woundHelpText(
    slope: number | undefined,
    limit: number,
    locale: string,
    t: Translate
): string {
    if (slope === undefined || Number.isNaN(slope)) return '';

    const growthText = formatFractionAsPercentageByLocale(slope, locale, 0);
    if (slope > -limit && slope < limit) return t('common.labels.stableText');
    if (slope >= limit)
        return `${t('common.labels.growingBy')} ${growthText} ${t('common.labels.eachWeek')}`;
    return `${t('common.labels.decreasingBy')} ${growthText} ${t('common.labels.eachWeek')}`;
}

/**
 * The pen's wound rate. Bioscope 1.0 measures no wound types, so this is the only wound row, for
 * smolt as for grow-out.
 */
function buildWoundRow(
    welfare: WelfareOverview | undefined,
    locale: string,
    t: Translate
): WelfareSummaryRow {
    const label = t('common.labels.wounds');
    const rate = welfare?.woundRate;

    // No overview and no rate read the same, and a trend beside a value the card calls missing
    // would describe nothing.
    if (!welfare || isMissing(rate)) {
        return {
            key: 'wounds',
            label,
            value: NO_VALUE,
            trend: TrendIconEnum.TrendNone,
            tooltip: t('common.labels.noDataFound'),
        };
    }

    const slope = welfare.woundSlope;
    const limit = settings.woundGrowthLimit;

    return {
        key: 'wounds',
        label,
        value: formatFractionAsPercentageByLocale(rate, locale, 1),
        trend: woundTrend(slope, limit),
        tooltip: woundHelpText(slope, limit, locale, t) || undefined,
    };
}

function buildSwimSpeedRow(
    swimSpeed: SwimSpeedOverview | undefined,
    hasSpeedAccess: boolean,
    locale: string,
    t: Translate
): WelfareSummaryRow {
    const label = t('common.labels.swimSpeed');
    const speed = swimSpeed?.swimSpeed;

    if (!hasSpeedAccess) {
        return { key: 'swimSpeed', label, value: NO_VALUE, trend: TrendIconEnum.TrendNone };
    }

    if (!speed) {
        return {
            key: 'swimSpeed',
            label,
            value: NO_VALUE,
            trend: TrendIconEnum.TrendNone,
            tooltip: t('common.labels.noDataFound'),
        };
    }

    return {
        key: 'swimSpeed',
        label,
        value: `${formatNumberByLocale(speed, locale, 2, NO_VALUE)} ${t('common.labels.swimspeed')}`,
        trend: TrendIconEnum.TrendNone,
        tooltip: calculateFishSpeedToolTip(getSpeedStatus(speed, t), { swimSpeed }, t),
    };
}

export interface BuildWelfareRowsParams {
    welfare: WelfareOverview | undefined;
    swimSpeed: SwimSpeedOverview | undefined;
    isSmolt: boolean;
    /** Either the FISH_SPEED or the BEHAVIOR licence. */
    hasSpeedAccess: boolean;
    locale: string;
    t: Translate;
}

/**
 * The rows the welfare card renders, derived in one pass.
 *
 * Pure and outside the component: these used to be a dozen `useState` values written from an
 * effect, which is why the page re-rendered twice for every welfare response.
 */
export function buildWelfareRows(params: BuildWelfareRowsParams): WelfareSummaryRow[] {
    const { welfare, swimSpeed, isSmolt, hasSpeedAccess, locale, t } = params;

    // Smolt production has no swim speed on this card.
    if (isSmolt) {
        return [buildWoundRow(welfare, locale, t)];
    }

    return [
        buildWoundRow(welfare, locale, t),
        buildSwimSpeedRow(swimSpeed, hasSpeedAccess, locale, t),
    ];
}
